import {
  type ReplaceSupplierCapabilityRow,
  type SupplierCapability,
  CAPABILITY_TAXONOMY,
  LEAF_INDEX,
  NONE_OF_THE_ABOVE
} from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'

import { extractErrorMessage } from '@/api/extract-error'

import { replaceSupplierCapabilities } from './api'
import {
  type CapabilitiesFormAction,
  type CapabilitiesFormState,
  type CapabilityLeafRow,
  CapabilitiesFormContext
} from './capabilities-context'
import { deriveCapabilities, isIncomplete } from './capabilities-derive'
import { invalidateOnboarding } from './invalidate-onboarding'

function emptyRow(): CapabilityLeafRow {
  return { yearsInBusiness: 0, inHouse: false, subContracted: false }
}

function initialState(initial: SupplierCapability[]): CapabilitiesFormState {
  const rows: Record<string, CapabilityLeafRow> = {}
  let noneSelected = false
  for (const cap of initial) {
    if (cap.capabilityItemKey === NONE_OF_THE_ABOVE) {
      noneSelected = true
      continue
    }
    rows[cap.capabilityItemKey] = {
      yearsInBusiness: cap.yearsInBusiness,
      inHouse: cap.inHouse,
      subContracted: cap.subContracted
    }
  }
  /** expand categories that already hold a pick so returning suppliers land on
   *  their selections; a fresh supplier sees every category collapsed. */
  const expandedCategories: Record<string, boolean> = {}
  for (const key of Object.keys(rows)) {
    const info = LEAF_INDEX[key]
    if (info) expandedCategories[info.categoryKey] = true
  }
  return {
    rows,
    noneSelected,
    query: '',
    expandedCategories,
    expandedGroups: {},
    submitError: null
  }
}

function reducer(
  state: CapabilitiesFormState,
  action: CapabilitiesFormAction
): CapabilitiesFormState {
  switch (action.type) {
    case 'toggleLeaf': {
      const rows = { ...state.rows }
      if (rows[action.leafKey]) delete rows[action.leafKey]
      else rows[action.leafKey] = emptyRow()
      return { ...state, rows, noneSelected: false }
    }
    case 'setYears': {
      const row = state.rows[action.leafKey]
      if (!row) return state
      return {
        ...state,
        rows: {
          ...state.rows,
          [action.leafKey]: { ...row, yearsInBusiness: action.years }
        }
      }
    }
    case 'toggleSourcing': {
      const row = state.rows[action.leafKey]
      if (!row) return state
      const next = !row[action.which]
      return {
        ...state,
        rows: {
          ...state.rows,
          [action.leafKey]: {
            ...row,
            inHouse: action.which === 'inHouse' ? next : false,
            subContracted: action.which === 'subContracted' ? next : false
          }
        }
      }
    }
    case 'toggleGroupAll': {
      const rows = { ...state.rows }
      for (const key of action.leafKeys) {
        if (action.select) {
          if (!rows[key]) rows[key] = emptyRow()
        } else {
          delete rows[key]
        }
      }
      /** selecting all marks every leaf incomplete (each still needs years +
       *  sourcing), so open the group or those required fields stay hidden.
       *  expand-only: deselecting must not yank the rows shut mid-edit. */
      const expandedGroups = action.select
        ? { ...state.expandedGroups, [action.groupKey]: true }
        : state.expandedGroups
      return { ...state, rows, expandedGroups, noneSelected: false }
    }
    case 'selectNone':
      return { ...state, rows: {}, noneSelected: true }
    case 'clearNone':
      return { ...state, noneSelected: false }
    case 'setQuery':
      return { ...state, query: action.query }
    case 'toggleCategory':
      return {
        ...state,
        expandedCategories: {
          ...state.expandedCategories,
          [action.categoryKey]: !state.expandedCategories[action.categoryKey]
        }
      }
    case 'toggleGroup':
      return {
        ...state,
        expandedGroups: {
          ...state.expandedGroups,
          [action.groupKey]: !state.expandedGroups[action.groupKey]
        }
      }
    case 'setExpandedAll': {
      const expandedCategories: Record<string, boolean> = {}
      const expandedGroups: Record<string, boolean> = {}
      if (action.expand) {
        for (const category of CAPABILITY_TAXONOMY) {
          expandedCategories[category.key] = true
          for (const group of category.groups) expandedGroups[group.key] = true
        }
      }
      return { ...state, expandedCategories, expandedGroups }
    }
    case 'submitError':
      return { ...state, submitError: action.message }
  }
}

export function SupplierCapabilitiesFormProvider({
  children,
  initial,
  onSuccess
}: {
  children: ReactNode
  initial: SupplierCapability[]
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, initial, initialState)
  const derived = deriveCapabilities(state)

  const mutation = useMutation({
    mutationFn: (capabilities: ReplaceSupplierCapabilityRow[]) =>
      replaceSupplierCapabilities({ capabilities }),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-capabilities')
      onSuccess()
    },
    onError: failure =>
      dispatch({ type: 'submitError', message: extractErrorMessage(failure) })
  })

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!derived.isValid || mutation.isPending) return
    dispatch({ type: 'submitError', message: null })

    const payload: ReplaceSupplierCapabilityRow[] = []
    if (state.noneSelected) {
      payload.push({
        capabilityItemKey: NONE_OF_THE_ABOVE,
        yearsInBusiness: 0,
        inHouse: false,
        subContracted: false
      })
    } else {
      for (const [key, row] of Object.entries(state.rows)) {
        if (isIncomplete(row)) continue
        payload.push({ capabilityItemKey: key, ...row })
      }
    }
    mutation.mutate(payload)
  }

  return (
    <CapabilitiesFormContext.Provider
      value={{
        state,
        derived,
        dispatch,
        isSubmitting: mutation.isPending,
        handleSubmit
      }}
    >
      {children}
    </CapabilitiesFormContext.Provider>
  )
}
